import { memo } from "react";
import { Button, Card, Icon, Tag, Text } from "@ttc3k/trekker";
import { EnumWidgetType } from "gql/generated";
import { ArrowRight, Pin, Star, StarSolid } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  DATE_MONTH_YEAR,
  formatDate,
  getMultilingualString,
} from "shared/utils";
import { Flex } from "styled-system/jsx";
import { Shimmer } from "shared/components";
import type { WidgetWithOptionalCount } from "apps/widgets/useWidgets";

type WidgetCardProps = {
  widget: WidgetWithOptionalCount;
  updatingWidgetId: string;
  countsLoading?: boolean;
  onFavoriteClick: (widget: WidgetWithOptionalCount) => void;
};

export const WidgetCard = memo(function WidgetCard({
  widget,
  onFavoriteClick,
  updatingWidgetId,
  countsLoading = false,
}: WidgetCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isPinnedAllMedia = !!widget.gallerySettings?.allMedia;

  const countText =
    widget.itemCount != null
      ? t("widgets:PAGE.ITEM", { count: widget.itemCount })
      : null;

  return (
    <Card.Surface
      boxShadow={"none"}
      borderLeftWidth={isPinnedAllMedia ? "3px" : undefined}
      borderLeftColor={isPinnedAllMedia ? "border.dark" : undefined}
      overflow="hidden"
    >
      <Card.Body
        backgroundColor={isPinnedAllMedia ? "bg.wa.lightest" : "white"}
        padding={"300"}
      >
        <Card.BodyContent alignItems={"center"} gap={"250"}>
          <Flex flexDir={"column"} gap={"50"} minW={0}>
            <Flex alignItems="center" gap="100" minW={0}>
              {isPinnedAllMedia ? (
                <Icon Element={Pin} size="sm" color="icon.mid" />
              ) : null}
              <Text visual={"bodySemiBold"} lineClamp={2}>
                {getMultilingualString(widget.name)}
              </Text>
            </Flex>
            <Flex alignItems="center" gap="50">
              {countText ? (
                <Text visual={"smallMedium"} color={"text.light"}>
                  {countText + " ・ "}
                </Text>
              ) : countsLoading ? (
                <>
                  <Shimmer h="14px" w="54px" display="inline-block" />
                  <Text visual={"smallMedium"} color={"text.light"}>
                    {" ・ "}
                  </Text>
                </>
              ) : null}
              <Text visual={"smallMedium"} color={"text.light"}>
                {"Updated: " +
                  formatDate(widget.updatedDateISO, DATE_MONTH_YEAR)}
              </Text>
            </Flex>
          </Flex>
          <Flex gap={"250"} alignItems={"center"}>
            <Tag
              visual={
                widget.type === EnumWidgetType.Operator
                  ? "highlight"
                  : widget.type === EnumWidgetType.Inquiry
                    ? "accent.blue"
                    : widget.type === EnumWidgetType.Gallery
                      ? "accent.purple"
                      : widget.type === EnumWidgetType.Promotion
                        ? "success"
                        : "accent.magenta"
              }
              mode="light"
            >
              {t(`widgets:LOOKUPS.TYPE.${widget.type}`)}
            </Tag>
            <Flex gap={"100"} alignItems={"center"}>
              <Button
                Icon={widget.favorited ? StarSolid : Star}
                color={widget.favorited ? "icon.highlight.light" : "icon.light"}
                visual={"primary"}
                mode={"light"}
                size={"sm"}
                onClick={() => onFavoriteClick(widget)}
                isLoading={updatingWidgetId === widget._id}
              />
              <Button
                Icon={ArrowRight}
                visual={"primary"}
                mode={"light"}
                size={"sm"}
                onClick={() => navigate(widget._id)}
              />
            </Flex>
          </Flex>
        </Card.BodyContent>
      </Card.Body>
    </Card.Surface>
  );
});
